import { labelWithSpecies } from 'common/utils/species'
import ThaiDatePicker from 'common/components/ThaiDatePicker'
import { ImageFile } from 'common/types/image'
import { errorMessageByField } from 'common/utils/hook-form'
import endOfToday from 'date-fns/endOfToday'
import { fNumber } from 'common/utils/formatNumber'
import SingleImageUpload from 'modules/inventory/components/ImageDropZone'
import { useSpeciesName } from 'modules/pond/api/selector'
import { SpeciesName } from 'modules/pond/constants'
import { useGetShrimpSalesTypes } from 'modules/shrimpSales/api/queries'
import { ShrimpCategoryRecord } from 'modules/shrimpSales/types'
import React from 'react'
import { Controller, SubmitHandler, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Grid, MenuItem } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import { UploadSingleFile } from 'components/upload'

import { computeSaleValue, ShrimpSalesFormDataType } from '.'
import GridTable from '../GridTable'
import NewUploadSingleFile from './NewUploadSingleFile'

interface DialogFormProps {
  onSubmit: SubmitHandler<ShrimpSalesFormDataType>
  onDropPhoto: (acceptedFiles: ImageFile[]) => void
  onCancel?: () => void
  step: 1 | 2
  onChangeStep?: (step: 1 | 2) => void
}

const DialogForm = ({ onSubmit, onDropPhoto, onCancel, step, onChangeStep }: DialogFormProps) => {
  const { t } = useTranslation('shrimpSales')
  const { data: shrimpSalesTypes } = useGetShrimpSalesTypes()
  const {
    control,
    formState: { errors },
    handleSubmit,
    trigger,
  } = useFormContext<ShrimpSalesFormDataType>()
  const speciesName = useSpeciesName()
  const watchedCategories = useWatch({ control, name: 'categories' }) as ShrimpCategoryRecord[]

  const goStep2 = async () => {
    const ok = await trigger(['date', 'buyer', 'shrimpTypeId', 'shrimpPerKg', 'image', 'note'])
    if (ok) onChangeStep?.(2)
  }

  const goStep1 = () => {
    onChangeStep?.(1)
  }

  return (
    <form
      id="create-shrimp-sales"
      onSubmit={(e) => e.preventDefault()}
      key="shrimp-sales-create-form"
    >
      <Stack spacing={2}>
        {step === 1 ? (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight={600} mb={0.5}>
                  <span style={{ color: 'red', marginLeft: 4 }}>* </span> {t('date')}
                </Typography>
                <Controller
                  name="date"
                  control={control}
                  render={({ field }) => (
                    <ThaiDatePicker
                      {...field}
                      format="dd/MM/yyyy"
                      maxDate={endOfToday()}
                      slotProps={{
                        textField: {
                          id: 'shrimp-sales-date-picker',
                          fullWidth: true,
                          size: 'small',
                          error: !!errors.date,
                          helperText: errorMessageByField('date', errors),
                          sx: { width: '100%' },
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight={600} mb={0.5}>
                  <span style={{ color: 'red', marginLeft: 4 }}>* </span> {t('buyer')}
                </Typography>
                <Controller
                  name="buyer"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder={t('buyer.placeholder')}
                      error={!!errors.buyer}
                      helperText={errorMessageByField('buyer', errors)}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight={600} mb={0.5}>
                  <span style={{ color: 'red', marginLeft: 4 }}>* </span> {t('shrimp.type')}
                </Typography>
                <Controller
                  name="shrimpTypeId"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      error={!!errors.shrimpTypeId}
                      helperText={errorMessageByField('shrimpTypeId', errors)}
                      fullWidth
                      size="small"
                      sx={{ minWidth: '150px' }}
                      SelectProps={{
                        displayEmpty: true,
                      }}
                    >
                      <MenuItem value={-1} disabled>
                        {t('choose.type')}
                      </MenuItem>
                      {Object.values(shrimpSalesTypes ?? []).map((el) => (
                        <MenuItem key={el.id} value={el.id}>
                          {el.type}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight={600} mb={0.5}>
                  <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                    *
                  </Box>{' '}
                  {labelWithSpecies(t('size'), speciesName)}
                </Typography>
                <Controller
                  name="shrimpPerKg"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      placeholder={t('amount.placeholder')}
                      error={!!errors.shrimpPerKg}
                      helperText={errorMessageByField('shrimpPerKg', errors)}
                      size="small"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {speciesName === SpeciesName.Fish
                              ? t('gram.per.one')
                              : t('shrimp.per.kg')}
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" fontWeight={600} mb={0.5}>
                  {t('note')}
                </Typography>
                <Controller
                  name="note"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      multiline
                      minRows={3}
                      placeholder={t('note.placeholder')}
                      error={!!errors.note}
                      helperText={errorMessageByField('note', errors)}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="image"
                  control={control}
                  render={({ field }) => (
                    <SingleImageUpload
                      initialUrl={typeof field.value === 'string' ? field.value : null}
                      errorText={
                        typeof errors.image?.message === 'string' ? errors.image.message : undefined
                      }
                      onChange={(file) => field.onChange(file)}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </>
        ) : (
          // step 2 เดิมของมึง
          <>
            <Typography fontWeight={600}>{t('more.detail')}</Typography>
            <GridTable isDialog />
          </>
        )}
      </Stack>

      <Box mt={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          {step === 2 ? (
            <Box>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                ฿{fNumber(computeSaleValue(watchedCategories ?? []))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('total.all')}
              </Typography>
            </Box>
          ) : (
            <Box />
          )}

          <Stack direction="row" spacing={2}>
            {step === 2 ? (
              <Button variant="outlined" color="inherit" type="button" onClick={goStep1}>
                {t('common:back')}
              </Button>
            ) : (
              <Button variant="outlined" color="inherit" type="button" onClick={onCancel}>
                {t('common:cancel')}
              </Button>
            )}

            {step === 1 ? (
              <Button variant="contained" color="primary" type="button" onClick={goStep2}>
                {t('common:next')}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                type="button"
                onClick={handleSubmit(onSubmit)}
              >
                {t('common:confirm')}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </form>
  )
}

export default DialogForm
